<template>
  <apexchart ref="chart" height="100%" :options="chartOptions" :series="chartSeries"></apexchart>
</template>

<script>
import { defaultChartOptions } from '@/utils/globalApexConfig';
import { formatCurrencyUSD } from '@/utils/funcs.js';
import {
  AMPLITUDE_SERIES,
  CLOSE_LONG_PURPOSES,
  CLOSE_PURPOSES,
  CLOSE_SHORT_PURPOSES,
  MOVEMENT_CHART_ID,
  SIBLING_PURPOSES,
  VOL_PER_AMP_SERIES,
  axisMaxFromValues,
  buildRightAxisYAxes,
  categoryXAxisOptions,
  extractLogDay,
  hideZeroDataLabel,
  manualChartPanOptions,
  readEventPnl,
  readLogAmplitude,
  readOrderNotional,
  readOrderQty,
  rightAxisMetricMixin,
  volumePerAmplitude,
} from '@/utils/manualChartDays.js';

export default {
  name: 'EvolManualMovementChart',
  mixins: [rightAxisMetricMixin],
  props: {
    logs: { type: Array, default: () => [] },
    rawData: { type: Array, default: () => [] },
    aggregatedData: { type: Array, default: () => [] },
  },
  computed: {
    allLogs() {
      if (this.logs && this.logs.length > 0) return this.logs;
      if (this.rawData && this.rawData.length > 0) return this.rawData;
      if (this.aggregatedData && this.aggregatedData.length > 0) return this.aggregatedData;
      return this.$store?.state?.eventLogs || [];
    },

    dailyBuckets() {
      const dayMap = new Map();

      this.allLogs.forEach(log => {
        if (log.processed === false) return;
        if (!SIBLING_PURPOSES.has(log.purpose)) return;

        const day = extractLogDay(log);
        if (!day) return;

        if (!dayMap.has(day.dayKey)) {
          dayMap.set(day.dayKey, {
            dayKey: day.dayKey,
            label: day.label,
            closeLongUSD: 0,
            closeShortUSD: 0,
            closeLongQty: 0,
            closeShortQty: 0,
            closeLongPnL: 0,
            closeShortPnL: 0,
            closeLongCount: 0,
            closeShortCount: 0,
            amplitude: null,
            volPerAmp: null,
          });
        }

        const bucket = dayMap.get(day.dayKey);
        const amplitude = readLogAmplitude(log);
        if (bucket.amplitude == null && amplitude != null) {
          bucket.amplitude = amplitude;
        }

        if (!CLOSE_PURPOSES.has(log.purpose)) return;

        const volumeUSD = readOrderNotional(log);
        const qty = readOrderQty(log);
        const pnl = readEventPnl(log);

        if (CLOSE_LONG_PURPOSES.has(log.purpose)) {
          bucket.closeLongUSD += volumeUSD;
          bucket.closeLongQty += qty;
          bucket.closeLongPnL += pnl;
          bucket.closeLongCount += 1;
        } else if (CLOSE_SHORT_PURPOSES.has(log.purpose)) {
          bucket.closeShortUSD += volumeUSD;
          bucket.closeShortQty += qty;
          bucket.closeShortPnL += pnl;
          bucket.closeShortCount += 1;
        }
      });

      const sorted = Array.from(dayMap.values()).sort((a, b) => a.dayKey.localeCompare(b.dayKey));
      sorted.forEach((bucket) => {
        bucket.volPerAmp = volumePerAmplitude(
          bucket.closeLongUSD + bucket.closeShortUSD,
          bucket.amplitude
        );
      });

      return {
        labels: sorted.map(d => d.label),
        closeLongUSD: sorted.map(d => parseFloat(d.closeLongUSD.toFixed(2))),
        closeShortUSD: sorted.map(d => parseFloat(d.closeShortUSD.toFixed(2))),
        amplitude: sorted.map(d => (d.amplitude == null ? null : parseFloat(d.amplitude.toFixed(3)))),
        volPerAmp: sorted.map(d => d.volPerAmp),
        rawBuckets: sorted,
      };
    },

    chartSeries() {
      return [
        { name: 'Close Long', type: 'bar', data: this.dailyBuckets.closeLongUSD },
        { name: 'Close Short',   type: 'bar', data: this.dailyBuckets.closeShortUSD },
        {
          name: AMPLITUDE_SERIES,
          type: 'area',
          data: this.dailyBuckets.amplitude,
          hidden: this.rightAxisMetric !== 'amplitude',
        },
        {
          name: VOL_PER_AMP_SERIES,
          type: 'area',
          data: this.dailyBuckets.volPerAmp,
          hidden: this.rightAxisMetric !== 'volPerAmp',
        },
      ];
    },

    amplitudeAxisMax() {
      return axisMaxFromValues(this.dailyBuckets.amplitude, 5);
    },

    volPerAmpAxisMax() {
      return axisMaxFromValues(this.dailyBuckets.volPerAmp, 100);
    },

    chartOptions() {
      const self = this;
      return {
        ...defaultChartOptions,
        chart: {
          ...defaultChartOptions.chart,
          ...manualChartPanOptions(),
          id: MOVEMENT_CHART_ID,
          type: 'bar',
          stacked: true,
          stackOnlyBar: true,
          events: {
            mounted(chartContext) {
              self.syncRightAxisSeries(chartContext);
              self.applyLatestXWindow(chartContext);
            },
            legendClick(chartContext, seriesIndex, opts) {
              self.handleRightAxisLegendClick(chartContext, seriesIndex, opts);
            },
            beforeResetZoom() {
              return self.latestResetZoomWindow();
            },
            zoomed(chartContext, opts) {
              self.onSiblingXWindow(chartContext, opts);
            },
            scrolled(chartContext, opts) {
              self.onSiblingXWindow(chartContext, opts);
            },
          },
        },
        title: {
          text: 'Fechamento de Posição',
          align: 'left',
          style: { color: '#f1f1f1', fontSize: '14px', fontWeight: 600 },
        },
        colors: ['#e66801', '#38bdf8', '#fbbf24', '#2dd4bf'],
        fill: { opacity: [0.85, 0.85, 0.35, 0.35] },
        stroke: { curve: 'smooth', width: [0, 0, 2, 2] },
        markers: { size: [0, 0, 3, 3], hover: { size: 4 }, strokeWidth: 0 },
        xaxis: categoryXAxisOptions(this.dailyBuckets.labels),
        yaxis: [
          {
            seriesName: 'Close Long',
            title: { text: 'Movimentado Diário (USD)' },
            labels: { formatter: formatCurrencyUSD },
          },
          {
            seriesName: 'Close Long',
            show: false,
          },
          ...buildRightAxisYAxes(this.rightAxisMetric, this.amplitudeAxisMax, this.volPerAmpAxisMax),
        ],
        dataLabels: {
          enabled: true,
          enabledOnSeries: [0, 1],
          formatter: hideZeroDataLabel,
        },
        plotOptions: {
          bar: {
            columnWidth: '60%',
            distributed: false,
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: function({ dataPointIndex }) {
            return self.generateTooltipHtml(dataPointIndex);
          },
        },
      };
    },
  },
  methods: {
    generateTooltipHtml(dataPointIndex) {
      const bucket = this.dailyBuckets.rawBuckets[dataPointIndex];
      if (!bucket) return '';

      const fmtUSD = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const fmtPnL = (v) => {
        const val = v || 0;
        const formatted = Math.abs(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return val >= 0 ? `+$${formatted}` : `-$${formatted}`;
      };
      const fmtPnLPct = (pnl, volume) => {
        if (!volume) return '';
        const pct = (pnl / volume) * 100;
        const sign = pct >= 0 ? '+' : '';
        return ` (${sign}${pct.toFixed(2)}%)`;
      };
      const fmtBTC = (v) => `${(v || 0).toFixed(4)} BTC`;
      const fmtAvgQty = (qty, count) => {
        if (!count) return '';
        return ` | média ${fmtBTC(qty / count)}`;
      };

      const totalUSD = bucket.closeLongUSD + bucket.closeShortUSD;
      const totalQty = bucket.closeLongQty + bucket.closeShortQty;
      const totalPnL = bucket.closeLongPnL + bucket.closeShortPnL;
      const totalPctColor = totalPnL >= 0 ? '#4ade80' : '#f87171';

      return `
        <div style="background-color: #1e1e1e; color: #fff; padding: 10px 14px; border-radius: 6px; border: 1px solid #444; font-size: 12px; box-shadow: 0 4px 16px rgba(0,0,0,0.7); min-width: 250px;">
          <div style="font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #444; padding-bottom: 4px; color: #38bdf8;">
            📅 Data: ${bucket.label}
          </div>
          <div style="margin-bottom: 8px;">
            <div style="color: #ef5350; font-weight: bold;">🔴 Close Long:</div>
            <div style="padding-left: 10px; line-height: 1.5;">
              Volume: <b>${fmtUSD(bucket.closeLongUSD)}</b> | P&L: <b style="color: ${bucket.closeLongPnL >= 0 ? '#4ade80' : '#f87171'}">${fmtPnL(bucket.closeLongPnL)}${fmtPnLPct(bucket.closeLongPnL, bucket.closeLongUSD)}</b><br/>
              Qtd: <b>${fmtBTC(bucket.closeLongQty)}</b> (${bucket.closeLongCount} ordens${fmtAvgQty(bucket.closeLongQty, bucket.closeLongCount)})
            </div>
          </div>
          <div style="margin-bottom: 8px;">
            <div style="color: #38bdf8; font-weight: bold;">🔵 Close Short:</div>
            <div style="padding-left: 10px; line-height: 1.5;">
              Volume: <b>${fmtUSD(bucket.closeShortUSD)}</b> | P&L: <b style="color: ${bucket.closeShortPnL >= 0 ? '#4ade80' : '#f87171'}">${fmtPnL(bucket.closeShortPnL)}${fmtPnLPct(bucket.closeShortPnL, bucket.closeShortUSD)}</b><br/>
              Qtd: <b>${fmtBTC(bucket.closeShortQty)}</b> (${bucket.closeShortCount} ordens${fmtAvgQty(bucket.closeShortQty, bucket.closeShortCount)})
            </div>
          </div>
          <div style="border-top: 1px solid #444; padding-top: 6px; color: #f1f1f1;">
            Total: <b>${fmtUSD(totalUSD)}</b> | <b>${fmtBTC(totalQty)}</b> | Lucro/mov.: <b style="color: ${totalPctColor}">${fmtPnL(totalPnL)}${fmtPnLPct(totalPnL, totalUSD)}</b>
            ${bucket.amplitude != null ? `<br/>Amplitude: <b style="color: #fbbf24">${bucket.amplitude.toFixed(2)}%</b>` : ''}
            ${bucket.volPerAmp != null ? `<br/>Vol / Amp: <b style="color: #2dd4bf">${fmtUSD(bucket.volPerAmp)}</b>` : ''}
          </div>
        </div>
      `;
    },
  },
};
</script>
